import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { classNames } from "primereact/utils";
import localeText from "../../../../locale";
import { isPathActive } from "../../config/adminPanelMenu";
import { logout } from "../../../../utilities/logout";
import logo from "../../../images/logo-color-horizontal.png";
import "./AdminSidebar.css";

const normalize = (value) =>
  (value || "")
    .toString()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const filterMenu = (menu, query) => {
  const term = normalize(query.trim());
  if (!term) return menu;

  return menu
    .map((section) => {
      const items = section.items
        .map((item) => {
          if (normalize(item.label).includes(term)) return item;
          if (!item.items) return null;
          const children = item.items.filter((child) => normalize(child.label).includes(term));
          return children.length ? { ...item, items: children } : null;
        })
        .filter(Boolean);
      return items.length ? { ...section, items } : null;
    })
    .filter(Boolean);
};

const getInitials = (firstname, surname) =>
  `${firstname?.charAt(0) || ""}${surname?.charAt(0) || ""}`.toUpperCase() || "A";

const AdminSidebar = ({ menu, userInfo, userType, isOpen, onNavigate, onSwitchVersion }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState({});

  const visibleMenu = useMemo(() => filterMenu(menu, query), [menu, query]);
  const isSearching = query.trim().length > 0;

  // Abre automaticamente o grupo que contém a página atual
  useEffect(() => {
    menu.forEach((section) =>
      section.items.forEach((item) => {
        if (item.items?.some((child) => isPathActive(child, pathname))) {
          setExpanded((prev) => (prev[item.key] ? prev : { ...prev, [item.key]: true }));
        }
      }),
    );
  }, [menu, pathname]);

  const toggleGroup = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const renderLink = (link, { child = false } = {}) => (
    <NavLink
      to={link.to}
      isActive={() => isPathActive(link, pathname)}
      className={classNames("admin-sidebar__link", { "admin-sidebar__link--child": child })}
      activeClassName="admin-sidebar__link--active"
      onClick={onNavigate}
    >
      {!child && <i className={link.icon} aria-hidden="true" />}
      <span>{link.label}</span>
    </NavLink>
  );

  const renderItem = (item) => {
    if (!item.items) {
      return (
        <li key={item.key} className="admin-sidebar__item">
          {renderLink(item)}
        </li>
      );
    }

    const hasActiveChild = item.items.some((child) => isPathActive(child, pathname));
    const isExpanded = isSearching || !!expanded[item.key];

    return (
      <li key={item.key} className="admin-sidebar__item">
        <button
          type="button"
          className={classNames("admin-sidebar__link admin-sidebar__group-toggle", {
            "admin-sidebar__link--has-active": hasActiveChild,
          })}
          aria-expanded={isExpanded}
          onClick={() => toggleGroup(item.key)}
        >
          <i className={item.icon} aria-hidden="true" />
          <span>{item.label}</span>
          <i
            className={classNames("pi pi-chevron-down admin-sidebar__chevron", {
              "admin-sidebar__chevron--open": isExpanded,
            })}
            aria-hidden="true"
          />
        </button>
        {isExpanded && (
          <ul className="admin-sidebar__sublist">
            {item.items.map((child) => (
              <li key={child.key}>{renderLink(child, { child: true })}</li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  const displayName = userInfo?.user?.firstname
    ? `${userInfo.user.firstname} ${userInfo.user.surname || ""}`.trim()
    : userType;

  return (
    <aside className={classNames("admin-sidebar", { "admin-sidebar--open": isOpen })}>
      <div className="admin-sidebar__brand">
        <button type="button" className="admin-sidebar__logo" onClick={() => history.push("/")}>
          <img src={logo} alt="extraconomy" />
        </button>
        <span className="admin-sidebar__badge">{userType === "Admin" ? "Admin" : userType}</span>
      </div>

      <div className="admin-sidebar__search">
        <i className="pi pi-search" aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={localeText("adminPanelSearchMenu")}
          aria-label={localeText("adminPanelSearchMenu")}
        />
      </div>

      <nav className="admin-sidebar__nav">
        {visibleMenu.map((section) => (
          <div key={section.key} className="admin-sidebar__section">
            <h3 className="admin-sidebar__section-title">{section.title}</h3>
            <ul className="admin-sidebar__list">{section.items.map(renderItem)}</ul>
          </div>
        ))}
        {visibleMenu.length === 0 && (
          <p className="admin-sidebar__empty">{localeText("adminPanelNoMenuResults")}</p>
        )}
      </nav>

      <div className="admin-sidebar__footer">
        <button type="button" className="admin-sidebar__switch" onClick={onSwitchVersion}>
          <i className="pi pi-history" aria-hidden="true" />
          <span>{localeText("adminPanelUseClassic")}</span>
        </button>
        <div className="admin-sidebar__profile">
          <div className="admin-sidebar__avatar">
            {userInfo?.user?.urlAvatar ? (
              <img src={userInfo.user.urlAvatar} alt="" />
            ) : (
              getInitials(userInfo?.user?.firstname, userInfo?.user?.surname)
            )}
          </div>
          <div className="admin-sidebar__profile-info">
            <div className="admin-sidebar__profile-name">{displayName}</div>
            <div className="admin-sidebar__profile-role">{userType}</div>
          </div>
          <button
            type="button"
            className="admin-sidebar__logout"
            onClick={logout}
            title={localeText("logout")}
            aria-label={localeText("logout")}
          >
            <i className="pi pi-sign-out" aria-hidden="true" />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default AdminSidebar;
